import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Grid, Edit, Merge, Split } from 'lucide-react';
import axios from 'axios';

// Create axios instance with dynamic token
const api = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL
});

// Add request interceptor to inject token dynamically
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    console.log("API Request - Token présent:", !!token);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      console.warn("⚠️ Aucun token trouvé dans localStorage");
    }
    return config;
  },
  (error) => {
    console.error("Request interceptor error:", error);
    return Promise.reject(error);
  }
);

// Add response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      console.error("❌ Erreur 401 - Token invalide ou expiré");
      alert("🔒 Session expirée. Veuillez vous reconnecter.");
      // Redirect to login
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

/**
 * ModularGridEditor - Éditeur de grille modulaire pour l'admin
 * Gère la configuration et l'édition de chaque cellule
 */
const ModularGridEditor = ({ content, onChange }) => {
  const [columns, setColumns] = useState(content?.columns || 2);
  const [rows, setRows] = useState(content?.rows || 2);
  const [cells, setCells] = useState(content?.cells || []);
  const [gridTitle, setGridTitle] = useState(content?.title || '');
  const [editingCell, setEditingCell] = useState(null);
  const [showCellModal, setShowCellModal] = useState(false);
  const [selectedCells, setSelectedCells] = useState([]);
  
  // États pour padding et gap
  const [internalPadding, setInternalPadding] = useState(content?.internal_padding ?? 16);
  const [gap, setGap] = useState(content?.gap ?? 16);
  
  // État pour border-radius global des cellules
  const [cellBorderRadius, setCellBorderRadius] = useState(content?.cell_border_radius ?? 8);
  
  // SECTION-LEVEL: fond global + padding de section
  const [sectionBgColor, setSectionBgColor] = useState(content?.background_color || '');
  const [sectionPadding, setSectionPadding] = useState(content?.section_padding ?? 0);
  
  // État pour hauteur fixe des cellules (0 = auto)
  const [cellHeight, setCellHeight] = useState(content?.cell_height ?? 0);
  
  // État pour hauteur par ligne (système avancé)
  // Format: [hauteur_ligne_1, hauteur_ligne_2, ...] (0 = auto pour cette ligne)
  const [rowHeights, setRowHeights] = useState(content?.row_heights || []);
  
  // Mode avancé pour afficher les contrôles par ligne
  const [useAdvancedRowHeights, setUseAdvancedRowHeights] = useState(
    Array.isArray(content?.row_heights) && content.row_heights.length > 0
  );

  useEffect(() => {
    // Initialize or update grid when size changes
    if (cells.length === 0) {
      initializeGrid();
    } else if (cells.length !== rows * columns) {
      updateGridSize();
    }
  }, [columns, rows]);

  const initializeGrid = () => {
    const newCells = [];
    const totalCells = rows * columns;
    
    for (let i = 0; i < totalCells; i++) {
      const row = Math.floor(i / columns);
      const col = i % columns;
      
      newCells.push({
        id: `cell-${row}-${col}`,
        row: row,
        col: col,
        rowspan: 1,
        colspan: 1,
        content_type: 'empty',
        data: {},
        disabled: false
      });
    }
    
    setCells(newCells);
    updateParent({ columns, rows, cells: newCells, title: gridTitle });
  };

  const updateGridSize = () => {
    const newTotalCells = rows * columns;
    const existingCells = [...cells];
    
    // Create map of existing cells by position
    const cellMap = new Map();
    existingCells.forEach(cell => {
      if (!cell.disabled) {
        const key = `${cell.row}-${cell.col}`;
        cellMap.set(key, cell);
      }
    });
    
    // Generate new grid preserving existing content
    const newCells = [];
    for (let i = 0; i < newTotalCells; i++) {
      const row = Math.floor(i / columns);
      const col = i % columns;
      const key = `${row}-${col}`;
      
      // Keep existing cell if it exists, otherwise create new empty cell
      if (cellMap.has(key)) {
        const existingCell = cellMap.get(key);
        newCells.push({
          ...existingCell,
          id: `cell-${row}-${col}`,
          row: row,
          col: col
        });
      } else {
        newCells.push({
          id: `cell-${row}-${col}`,
          row: row,
          col: col,
          rowspan: 1,
          colspan: 1,
          content_type: 'empty',
          data: {},
          disabled: false
        });
      }
    }
    
    setCells(newCells);
    updateParent({ columns, rows, cells: newCells, title: gridTitle });
  };

  const updateParent = (data) => {
    onChange({
      title: data.title !== undefined ? data.title : gridTitle,
      columns: data.columns || columns,
      rows: data.rows || rows,
      cells: data.cells || cells,
      internal_padding: data.internal_padding !== undefined ? data.internal_padding : internalPadding,
      gap: data.gap !== undefined ? data.gap : gap,
      cell_border_radius: data.cell_border_radius !== undefined ? data.cell_border_radius : cellBorderRadius,
      cell_height: data.cell_height !== undefined ? data.cell_height : cellHeight,
      row_heights: data.row_heights !== undefined ? data.row_heights : rowHeights,
      background_color: data.background_color !== undefined ? data.background_color : sectionBgColor,
      section_padding: data.section_padding !== undefined ? data.section_padding : sectionPadding
    });
  };

  const handleGridSizeChange = (newCols, newRows) => {
    setColumns(newCols);
    setRows(newRows);
    // Grid will reinitialize via useEffect
  };

  const handleTitleChange = (newTitle) => {
    setGridTitle(newTitle);
    updateParent({ title: newTitle });
  };

  const toggleCellSelection = (cell) => {
    if (cell.disabled) return;
    
    const isSelected = selectedCells.some(c => c.id === cell.id);
    
    if (isSelected) {
      setSelectedCells(selectedCells.filter(c => c.id !== cell.id));
    } else {
      if (selectedCells.length < 2) {
        setSelectedCells([...selectedCells, cell]);
      } else {
        setSelectedCells([cell]);
      }
    }
  };

  const canMergeCells = () => {
    if (selectedCells.length !== 2) return false;
    
    const [cell1, cell2] = selectedCells;
    
    // Check if horizontal adjacency
    const horizontalAdj = cell1.row === cell2.row && Math.abs(cell1.col - cell2.col) === 1;
    
    // Check if vertical adjacency
    const verticalAdj = cell1.col === cell2.col && Math.abs(cell1.row - cell2.row) === 1;
    
    return horizontalAdj || verticalAdj;
  };

  const mergeCells = () => {
    if (!canMergeCells()) return;
    
    const [cell1, cell2] = selectedCells;
    const isHorizontal = cell1.row === cell2.row;
    
    // Determine primary cell (top-left)
    const primaryCell = isHorizontal 
      ? (cell1.col < cell2.col ? cell1 : cell2)
      : (cell1.row < cell2.row ? cell1 : cell2);
    
    const updatedCells = cells.map(c => {
      if (c.id === primaryCell.id) {
        return {
          ...c,
          colspan: isHorizontal ? 2 : 1,
          rowspan: isHorizontal ? 1 : 2
        };
      }
      if (c.id === (isHorizontal ? (cell1.col < cell2.col ? cell2 : cell1) : (cell1.row < cell2.row ? cell2 : cell1)).id) {
        return {
          ...c,
          disabled: true,
          content_type: 'empty',
          data: {}
        };
      }
      return c;
    });
    
    setCells(updatedCells);
    updateParent({ cells: updatedCells });
    setSelectedCells([]);
  };

  const splitCell = (cell) => {
    if (cell.colspan === 1 && cell.rowspan === 1) {
      alert('Cette cellule n\'est pas fusionnée');
      return;
    }

    const updatedCells = cells.map(c => {
      // Reset the merged cell
      if (c.id === cell.id) {
        return {
          ...c,
          colspan: 1,
          rowspan: 1
        };
      }

      // Re-enable disabled cells that were part of the merge
      // Check if cell was in the merge area
      const isInMergeArea = 
        c.row >= cell.row && 
        c.row < cell.row + cell.rowspan &&
        c.col >= cell.col && 
        c.col < cell.col + cell.colspan &&
        c.disabled;

      if (isInMergeArea && c.id !== cell.id) {
        return {
          ...c,
          disabled: false,
          colspan: 1,
          rowspan: 1,
          content_type: 'empty',
          data: {}
        };
      }

      return c;
    });

    setCells(updatedCells);
    updateParent({ cells: updatedCells });
    alert('✅ Cellule divisée avec succès');
  };

  const handleEditCell = (cell) => {
    // Initialize padding_detail if not exists
    const cellWithDefaults = {
      ...cell,
      data: {
        ...cell.data,
        padding_detail: cell.data?.padding_detail || {
          top: 16,
          right: 16,
          bottom: 16,
          left: 16
        }
      }
    };
    
    setEditingCell(cellWithDefaults);
    
    // Initialize image/video mode based on existing data
    if (cell.content_type === 'image') {
      const hasUploadedImage = !!cell.data?.uploaded_image_url;
      const hasImageUrl = !!cell.data?.image_url;
      setImageMode(hasUploadedImage ? 'upload' : 'url');
    } else if (cell.content_type === 'video') {
      const hasUploadedVideo = !!cell.data?.uploaded_video_url;
      const hasVideoUrl = !!cell.data?.video_url;
      setVideoMode(hasUploadedVideo ? 'upload' : 'url');
    }
    
    setShowCellModal(true);
  };

  const handleSaveCell = () => {
    const updatedCells = cells.map(c => 
      c.id === editingCell.id ? editingCell : c
    );
    setCells(updatedCells);
    updateParent({ cells: updatedCells });
    setShowCellModal(false);
    setEditingCell(null);
  };

  const handleCellContentTypeChange = (type) => {
    setEditingCell({
      ...editingCell,
      content_type: type,
      data: {}  // Reset data when type changes
    });
  };

  const handleCellDataChange = (field, value) => {
    setEditingCell({
      ...editingCell,
      data: {
        ...editingCell.data,
        [field]: value
      }
    });
  };

  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [imageMode, setImageMode] = useState('url'); // 'url' or 'upload'
  const [videoMode, setVideoMode] = useState('url'); // 'url' or 'upload'

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    console.log("═══════════════════════════════════════════");
    console.log("UPLOAD TRIGGERED - handleImageUpload");
    console.log("FILE SELECTED:", file);
    
    // Verify token exists
    const token = localStorage.getItem('token');
    console.log("Token check:", token ? `Token présent (${token.substring(0, 20)}...)` : "❌ AUCUN TOKEN");
    
    if (!token) {
      alert("🔒 Session expirée. Veuillez vous reconnecter.");
      window.location.href = '/login';
      return;
    }
    
    if (!file) {
      console.log("No file selected");
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('⚠️ Veuillez sélectionner une image (JPEG, PNG, GIF, WebP)');
      console.error("Invalid file type:", file.type);
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('⚠️ L\'image est trop volumineuse (max 5MB)');
      console.error("File too large:", file.size, "bytes");
      return;
    }

    console.log("✅ File validation passed");
    console.log("   - Type:", file.type);
    console.log("   - Size:", (file.size / 1024).toFixed(2), "KB");
    setUploadingImage(true);

    // Upload to backend
    const formData = new FormData();
    formData.append('file', file);

    try {
      console.log("📤 Sending upload request to /api/upload");
      console.log("   - Endpoint:", process.env.REACT_APP_BACKEND_URL + '/api/upload');
      console.log("   - Token will be added by interceptor");
      
      const response = await api.post('/api/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      console.log("✅ Upload response:", response.data);
      console.log("   - URL:", response.data.url);
      console.log("   - Filename:", response.data.filename);

      // Store URL in current cell only
      setEditingCell({
        ...editingCell,
        data: {
          ...editingCell.data,
          uploaded_image_url: response.data.url,
          image_url: ''  // Clear URL field
        }
      });
      
      // Success feedback
      alert('✅ Image uploadée avec succès !');
      console.log("✅ Image uploaded successfully:", response.data.url);
      console.log("═══════════════════════════════════════════");
    } catch (error) {
      console.error("❌ Upload error:", error);
      console.error("   - Status:", error.response?.status);
      console.error("   - Detail:", error.response?.data?.detail);
      console.error("   - Message:", error.message);
      console.log("═══════════════════════════════════════════");
      
      let errorMessage = 'Erreur lors de l\'upload';
      
      if (error.response?.status === 401) {
        errorMessage = 'Session expirée. Veuillez vous reconnecter.';
        // Interceptor will redirect to login
      } else if (error.response?.status === 413) {
        errorMessage = 'Fichier trop volumineux.';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      alert('❌ ' + errorMessage);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    console.log("═══════════════════════════════════════════");
    console.log("VIDEO UPLOAD TRIGGERED - handleVideoUpload");
    console.log("VIDEO FILE SELECTED:", file);
    
    // Verify token exists
    const token = localStorage.getItem('token');
    console.log("Token check:", token ? `Token présent (${token.substring(0, 20)}...)` : "❌ AUCUN TOKEN");
    
    if (!token) {
      alert("🔒 Session expirée. Veuillez vous reconnecter.");
      window.location.href = '/login';
      return;
    }
    
    if (!file) {
      console.log("No video file selected");
      return;
    }

    // Validate file type
    if (!file.type.startsWith('video/')) {
      alert('⚠️ Veuillez sélectionner une vidéo (MP4, WebM, OGG)');
      console.error("Invalid video file type:", file.type);
      return;
    }

    // Validate file size (max 50MB)
    if (file.size > 50 * 1024 * 1024) {
      alert('⚠️ La vidéo est trop volumineuse (max 50MB)');
      console.error("Video file too large:", file.size, "bytes");
      return;
    }

    console.log("✅ Video file validation passed");
    console.log("   - Type:", file.type);
    console.log("   - Size:", (file.size / 1024 / 1024).toFixed(2), "MB");
    setUploadingVideo(true);

    // Upload to backend
    const formData = new FormData();
    formData.append('file', file);

    try {
      console.log("📤 Sending video upload request to /api/upload");
      console.log("   - Endpoint:", process.env.REACT_APP_BACKEND_URL + '/api/upload');
      console.log("   - Token will be added by interceptor");
      
      const response = await api.post('/api/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      console.log("✅ Video upload response:", response.data);
      console.log("   - URL:", response.data.url);
      console.log("   - Filename:", response.data.filename);

      // Store URL in current cell only
      setEditingCell({
        ...editingCell,
        data: {
          ...editingCell.data,
          uploaded_video_url: response.data.url,
          video_url: ''  // Clear URL field
        }
      });
      
      // Success feedback
      alert('✅ Vidéo uploadée avec succès !');
      console.log("✅ Video uploaded successfully:", response.data.url);
      console.log("═══════════════════════════════════════════");
    } catch (error) {
      console.error("❌ Video upload error:", error);
      console.error("   - Status:", error.response?.status);
      console.error("   - Detail:", error.response?.data?.detail);
      console.error("   - Message:", error.message);
      console.log("═══════════════════════════════════════════");
      
      let errorMessage = 'Erreur lors de l\'upload de la vidéo';
      
      if (error.response?.status === 401) {
        errorMessage = 'Session expirée. Veuillez vous reconnecter.';
        // Interceptor will redirect to login
      } else if (error.response?.status === 413) {
        errorMessage = 'Vidéo trop volumineuse.';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      alert('❌ ' + errorMessage);
    } finally {
      setUploadingVideo(false);
    }
  };

  const renderCellContentEditor = () => {
    if (!editingCell) return null;

    switch (editingCell.content_type) {
      case 'title':
        return (
          <>
            <div className="space-y-2">
              <Label>Texte du titre *</Label>
              <Input
                value={editingCell.data.text || ''}
                onChange={(e) => handleCellDataChange('text', e.target.value)}
                placeholder="Saisissez le titre"
              />
            </div>
            
            {/* Alignement du texte */}
            <div className="space-y-2">
              <Label>Alignement du texte</Label>
              <Select
                value={editingCell.data.text_align || 'left'}
                onValueChange={(value) => handleCellDataChange('text_align', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choisir un alignement" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="left">⬅️ Gauche</SelectItem>
                  <SelectItem value="center">↔️ Centré</SelectItem>
                  <SelectItem value="right">➡️ Droite</SelectItem>
                  <SelectItem value="justify">↕️ Justifié</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </>
        );

      case 'text':
        return (
          <>
            <div className="space-y-2">
              <Label>Titre (optionnel)</Label>
              <Input
                value={editingCell.data.title || ''}
                onChange={(e) => handleCellDataChange('title', e.target.value)}
                placeholder="Titre de la section"
              />
            </div>
            <div className="space-y-2">
              <Label>Texte *</Label>
              <Textarea
                value={editingCell.data.text || ''}
                onChange={(e) => handleCellDataChange('text', e.target.value)}
                placeholder="Saisissez votre texte... Vous pouvez utiliser des émojis 😊"
                rows={4}
              />
            </div>
            
            {/* Alignement du texte */}
            <div className="space-y-2">
              <Label>Alignement du texte</Label>
              <Select
                value={editingCell.data.text_align || 'left'}
                onValueChange={(value) => handleCellDataChange('text_align', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choisir un alignement" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="left">⬅️ Gauche</SelectItem>
                  <SelectItem value="center">↔️ Centré</SelectItem>
                  <SelectItem value="right">➡️ Droite</SelectItem>
                  <SelectItem value="justify">↕️ Justifié</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </>
        );

      case 'image':
        return (
          <div className="space-y-4">
            {/* Mode selector buttons */}
            <div className="flex gap-2">
              <Button
                type="button"
                variant={imageMode === 'url' ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  console.log("CLICK URL MODE");
                  // Switch to URL mode and clear upload
                  setImageMode('url');
                  setEditingCell({
                    ...editingCell,
                    data: {
                      ...editingCell.data,
                      uploaded_image_url: '',
                      image_url: editingCell.data.image_url || ''
                    }
                  });
                }}
              >
                URL
              </Button>
              <Button
                type="button"
                variant={imageMode === 'upload' ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  console.log("CLICK UPLOAD MODE");
                  // Switch to Upload mode and clear URL
                  setImageMode('upload');
                  setEditingCell({
                    ...editingCell,
                    data: {
                      ...editingCell.data,
                      image_url: '',
                      uploaded_image_url: editingCell.data.uploaded_image_url || ''
                    }
                  });
                }}
              >
                Upload
              </Button>
            </div>

            {/* Current mode indicator */}
            <div className="text-xs text-muted-foreground bg-blue-50 p-2 rounded border border-blue-200">
              {imageMode === 'url' 
                ? '🔗 Mode URL : Saisissez une URL externe' 
                : '📤 Mode Upload : Sélectionnez un fichier depuis votre ordinateur'}
            </div>

            {/* URL field - only shown in URL mode */}
            {imageMode === 'url' && (
              <div className="space-y-2">
                <Label htmlFor="image-url-input">URL de l'image</Label>
                <Input
                  id="image-url-input"
                  value={editingCell.data.image_url || ''}
                  onChange={(e) => {
                    console.log("URL changed:", e.target.value);
                    handleCellDataChange('image_url', e.target.value);
                  }}
                  placeholder="https://exemple.com/image.jpg"
                />
                {editingCell.data.image_url && (
                  <div className="mt-2 p-2 border rounded bg-gray-50">
                    <img 
                      src={editingCell.data.image_url} 
                      alt="Preview" 
                      className="max-h-48 max-w-full rounded border object-contain mx-auto"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Upload field - only shown in Upload mode */}
            {imageMode === 'upload' && (
              <div className="space-y-2">
                <Label htmlFor="image-file-input">Sélectionner une image</Label>
                <div className="relative">
                  <Input
                    id="image-file-input"
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      console.log("FILE SELECTED:", e.target.files[0]);
                      handleImageUpload(e);
                    }}
                    disabled={uploadingImage}
                    className="cursor-pointer file:cursor-pointer file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-primary-foreground hover:file:bg-primary/90"
                    style={{ pointerEvents: 'auto' }}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Formats supportés : JPEG, PNG, GIF, WebP (max 5MB)
                </p>
                {uploadingImage && (
                  <div className="text-sm text-blue-600 font-medium flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    ⏳ Upload en cours...
                  </div>
                )}
                {editingCell.data.uploaded_image_url && !uploadingImage && (
                  <div className="mt-2 p-2 border rounded bg-gray-50">
                    <img 
                      src={editingCell.data.uploaded_image_url} 
                      alt="Preview" 
                      className="max-h-48 max-w-full rounded border object-contain mx-auto"
                    />
                    <p className="text-xs text-green-600 mt-2 text-center font-medium">✅ Image uploadée</p>
                  </div>
                )}
              </div>
            )}

            {/* Lien de l'image (optionnel) - Rend l'image cliquable */}
            <div className="space-y-2 border-t pt-4">
              <Label htmlFor="image-link">
                Lien de l'image (optionnel)
                <span className="text-xs text-muted-foreground ml-2">
                  Rend l'image cliquable
                </span>
              </Label>
              <Input
                id="image-link"
                type="url"
                value={editingCell.data.imageLink || ''}
                onChange={(e) => handleCellDataChange('imageLink', e.target.value)}
                placeholder="https://... (optionnel)"
              />
              <p className="text-xs text-muted-foreground">
                {editingCell.data.imageLink 
                  ? `🔗 Image cliquable → ${editingCell.data.imageLink}`
                  : 'Laisser vide pour une image non-cliquable'
                }
              </p>
            </div>
          </div>
        );

      case 'button':
        return (
          <>
            <div className="space-y-2">
              <Label>Texte du bouton *</Label>
              <Input
                value={editingCell.data.text || ''}
                onChange={(e) => handleCellDataChange('text', e.target.value)}
                placeholder="Cliquez ici"
              />
            </div>
            <div className="space-y-2">
              <Label>Lien du bouton</Label>
              <Input
                value={editingCell.data.link || ''}
                onChange={(e) => handleCellDataChange('link', e.target.value)}
                placeholder="/dashboard/xxx"
              />
            </div>
            
            {/* Alignement du bouton */}
            <div className="space-y-2">
              <Label>Alignement du bouton</Label>
              <Select
                value={editingCell.data.text_align || 'left'}
                onValueChange={(value) => handleCellDataChange('text_align', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choisir un alignement" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="left">⬅️ Gauche</SelectItem>
                  <SelectItem value="center">↔️ Centré</SelectItem>
                  <SelectItem value="right">➡️ Droite</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </>
        );

      case 'video':
        return (
          <div className="space-y-4">
            {/* Mode selector buttons */}
            <div className="flex gap-2">
              <Button
                type="button"
                variant={videoMode === 'url' ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  console.log("CLICK VIDEO URL MODE");
                  // Switch to URL mode and clear upload
                  setVideoMode('url');
                  setEditingCell({
                    ...editingCell,
                    data: {
                      ...editingCell.data,
                      uploaded_video_url: '',
                      video_url: editingCell.data.video_url || ''
                    }
                  });
                }}
              >
                URL
              </Button>
              <Button
                type="button"
                variant={videoMode === 'upload' ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  console.log("CLICK VIDEO UPLOAD MODE");
                  // Switch to Upload mode and clear URL
                  setVideoMode('upload');
                  setEditingCell({
                    ...editingCell,
                    data: {
                      ...editingCell.data,
                      video_url: '',
                      uploaded_video_url: editingCell.data.uploaded_video_url || ''
                    }
                  });
                }}
              >
                Upload
              </Button>
            </div>

            {/* Current mode indicator */}
            <div className="text-xs text-muted-foreground bg-blue-50 p-2 rounded border border-blue-200">
              {videoMode === 'url' 
                ? '🔗 Mode URL : Saisissez une URL YouTube, Vimeo ou directe' 
                : '📤 Mode Upload : Sélectionnez un fichier vidéo depuis votre ordinateur'}
            </div>

            {/* URL field - only shown in URL mode */}
            {videoMode === 'url' && (
              <div className="space-y-2">
                <Label htmlFor="video-url-input">URL de la vidéo</Label>
                <Input
                  id="video-url-input"
                  value={editingCell.data.video_url || ''}
                  onChange={(e) => {
                    console.log("VIDEO URL changed:", e.target.value);
                    handleCellDataChange('video_url', e.target.value);
                  }}
                  placeholder="https://youtube.com/watch?v=... ou https://..."
                />
                <p className="text-xs text-muted-foreground">
                  Supporte YouTube, Vimeo, ou lien direct vers vidéo
                </p>
              </div>
            )}

            {/* Upload field - only shown in Upload mode */}
            {videoMode === 'upload' && (
              <div className="space-y-2">
                <Label htmlFor="video-file-input">Sélectionner une vidéo</Label>
                <div className="relative">
                  <Input
                    id="video-file-input"
                    type="file"
                    accept="video/*"
                    onChange={(e) => {
                      console.log("VIDEO FILE SELECTED:", e.target.files[0]);
                      handleVideoUpload(e);
                    }}
                    disabled={uploadingVideo}
                    className="cursor-pointer file:cursor-pointer file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-primary-foreground hover:file:bg-primary/90"
                    style={{ pointerEvents: 'auto' }}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Formats supportés : MP4, WebM, OGG (max 50MB)
                </p>
                {uploadingVideo && (
                  <div className="text-sm text-blue-600 font-medium flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    ⏳ Upload en cours...
                  </div>
                )}
                {editingCell.data.uploaded_video_url && !uploadingVideo && (
                  <div className="mt-2 p-2 border rounded bg-gray-50">
                    <video 
                      src={editingCell.data.uploaded_video_url} 
                      className="max-h-48 max-w-full rounded border mx-auto"
                      controls
                    />
                    <p className="text-xs text-green-600 mt-2 text-center font-medium">✅ Vidéo uploadée</p>
                  </div>
                )}
              </div>
            )}
          </div>
        );

      default:
        return (
          <p className="text-muted-foreground text-sm">
            Sélectionnez un type de contenu ci-dessus
          </p>
        );
    }
  };

  return (
    <div className="space-y-4">
      {/* Titre du bloc */}
      <div className="space-y-2">
        <Label>Titre du bloc (optionnel)</Label>
        <Input
          value={gridTitle}
          onChange={(e) => handleTitleChange(e.target.value)}
          placeholder="Ex: Nos Services"
        />
        <p className="text-xs text-muted-foreground">
          Si renseigné, sera affiché au-dessus de la grille côté magasin
        </p>
      </div>

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* 🟢 SECTION — Apparence globale du bloc                     */}
      {/* ═══════════════════════════════════════════════════════════ */}
      <div className="border rounded-lg p-4 space-y-4 bg-emerald-50/30" style={sectionBgColor ? { backgroundColor: `${sectionBgColor}15` } : undefined}>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-emerald-500" />
          <Label className="text-base font-semibold block">Section</Label>
          <span className="text-xs text-muted-foreground">Apparence globale du bloc</span>
        </div>

        {/* Couleur de fond du bloc */}
        <div className="space-y-2">
          <Label>Couleur de fond</Label>
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={sectionBgColor || '#ffffff'}
              onChange={(e) => {
                setSectionBgColor(e.target.value);
                updateParent({ background_color: e.target.value });
              }}
              className="w-10 h-10 rounded cursor-pointer border"
              data-testid="input-section-bg-color"
            />
            <Input
              value={sectionBgColor || ''}
              onChange={(e) => {
                setSectionBgColor(e.target.value);
                updateParent({ background_color: e.target.value });
              }}
              placeholder="Ex: #f5f0e8"
              className="flex-1"
            />
            {sectionBgColor && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSectionBgColor('');
                  updateParent({ background_color: '' });
                }}
                data-testid="reset-section-bg-color"
              >
                Réinitialiser
              </Button>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            Visible dans les espaces entre les cellules et autour de la grille
          </p>
        </div>

        {/* Padding de section */}
        <div className="space-y-2">
          <Label htmlFor="sectionPadding">
            Padding de section (px)
            <span className="text-xs text-muted-foreground ml-2">Espace entre le bord du fond et la grille</span>
          </Label>
          <div className="flex items-center gap-3">
            <Input
              id="sectionPadding"
              type="number"
              min={0}
              max={100}
              value={sectionPadding}
              onChange={(e) => {
                const val = Math.max(0, parseInt(e.target.value) || 0);
                setSectionPadding(val);
                updateParent({ section_padding: val });
              }}
              className="w-24"
              data-testid="input-section-padding"
            />
            <input
              type="range"
              min="0"
              max="100"
              value={sectionPadding}
              onChange={(e) => {
                const val = parseInt(e.target.value);
                setSectionPadding(val);
                updateParent({ section_padding: val });
              }}
              className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
            />
            <span className="text-sm text-muted-foreground w-16 text-right">
              {sectionPadding}px
            </span>
          </div>
        </div>

        {/* Aperçu section */}
        {sectionBgColor && (
          <div 
            className="rounded-lg border overflow-hidden"
            style={{ backgroundColor: sectionBgColor, padding: `${Math.max(sectionPadding, 8)}px` }}
          >
            <div className="grid grid-cols-3 gap-2">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-white rounded h-6 opacity-80" />
              ))}
            </div>
            <p className="text-xs text-center mt-2 opacity-60">Aperçu section</p>
          </div>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* 🟡 LAYOUT — Structure de la grille                         */}
      {/* ═══════════════════════════════════════════════════════════ */}
      <div className="border rounded-lg p-4 bg-amber-50/30 space-y-4">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-amber-500" />
          <Label className="text-base font-semibold block">Layout</Label>
          <span className="text-xs text-muted-foreground">Structure de la grille</span>
        </div>
        
        {/* Dimensions */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Nombre de colonnes *</Label>
            <Input
              type="number"
              min={1}
              max={6}
              value={columns}
              onChange={(e) => handleGridSizeChange(parseInt(e.target.value) || 1, rows)}
            />
          </div>
          <div className="space-y-2">
            <Label>Nombre de lignes *</Label>
            <Input
              type="number"
              min={1}
              max={6}
              value={rows}
              onChange={(e) => handleGridSizeChange(columns, parseInt(e.target.value) || 1)}
            />
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Grille actuelle : {columns} colonnes × {rows} lignes = {columns * rows} cellules
        </p>

        {/* Gap entre cellules — LAYOUT level */}
        <div className="space-y-2">
          <Label htmlFor="gap">
            Espacement entre les cellules (px)
            <span className="text-xs text-muted-foreground ml-2">Espace entre chaque cellule</span>
          </Label>
          <div className="flex items-center gap-3">
            <Input
              id="gap"
              type="number"
              min={0}
              max={100}
              value={gap}
              onChange={(e) => {
                const val = Math.max(0, parseInt(e.target.value) || 0);
                setGap(val);
                updateParent({ gap: val });
              }}
              className="w-24"
            />
            <input
              type="range"
              min="0"
              max="100"
              value={gap}
              onChange={(e) => {
                const val = parseInt(e.target.value);
                setGap(val);
                updateParent({ gap: val });
              }}
              className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
            />
            <span className="text-sm text-muted-foreground w-16 text-right">
              {gap}px
            </span>
          </div>
        </div>

        {/* Hauteur des cellules — LAYOUT level (structure) */}
        <div className="space-y-4">
            {/* Toggle Mode Avancé */}
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-2">
                Mode de hauteur
              </Label>
              <div className="flex items-center gap-2">
                <span className={`text-xs ${!useAdvancedRowHeights ? 'font-semibold text-primary' : 'text-muted-foreground'}`}>
                  Uniforme
                </span>
                <button
                  type="button"
                  onClick={() => {
                    const newMode = !useAdvancedRowHeights;
                    setUseAdvancedRowHeights(newMode);
                    if (newMode) {
                      // Initialiser row_heights avec la valeur actuelle de cellHeight ou 0
                      const initialHeights = Array(rows).fill(cellHeight || 0);
                      setRowHeights(initialHeights);
                      updateParent({ row_heights: initialHeights });
                    } else {
                      // Désactiver mode avancé : vider row_heights
                      setRowHeights([]);
                      updateParent({ row_heights: [] });
                    }
                  }}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    useAdvancedRowHeights ? 'bg-primary' : 'bg-gray-200'
                  }`}
                  data-testid="toggle-advanced-row-heights"
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      useAdvancedRowHeights ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
                <span className={`text-xs ${useAdvancedRowHeights ? 'font-semibold text-primary' : 'text-muted-foreground'}`}>
                  Par ligne
                </span>
              </div>
            </div>

            {!useAdvancedRowHeights ? (
              /* Mode Uniforme (Legacy) - cell_height */
              <div className="space-y-2">
                <Label htmlFor="cellHeight">
                  Hauteur uniforme (px)
                  <span className="text-xs text-muted-foreground ml-2">0 = auto</span>
                </Label>
                <div className="flex items-center gap-3">
                  <Input
                    id="cellHeight"
                    type="number"
                    min={0}
                    max={500}
                    value={cellHeight}
                    onChange={(e) => {
                      const val = Math.max(0, parseInt(e.target.value) || 0);
                      setCellHeight(val);
                      updateParent({ cell_height: val });
                    }}
                    className="w-24"
                    data-testid="input-cell-height"
                  />
                  <input
                    type="range"
                    min="0"
                    max="500"
                    step="10"
                    value={cellHeight}
                    onChange={(e) => {
                      const val = parseInt(e.target.value);
                      setCellHeight(val);
                      updateParent({ cell_height: val });
                    }}
                    className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
                  />
                  <span className="text-sm text-muted-foreground w-16 text-right">
                    {cellHeight === 0 ? 'auto' : `${cellHeight}px`}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Applique une hauteur identique à toutes les cellules.
                </p>
              </div>
            ) : (
              /* Mode Avancé - row_heights */
              <div className="space-y-3">
                <Label>
                  Hauteur par ligne (px)
                  <span className="text-xs text-muted-foreground ml-2">0 = auto</span>
                </Label>
                <div className="space-y-2 max-h-48 overflow-y-auto pr-2">
                  {Array.from({ length: rows }, (_, rowIndex) => {
                    const rowHeight = rowHeights[rowIndex] ?? 0;
                    return (
                      <div key={rowIndex} className="flex items-center gap-3 bg-gray-50 rounded-lg p-2">
                        <span className="text-xs font-medium text-gray-600 w-16">
                          Ligne {rowIndex + 1}
                        </span>
                        <Input
                          type="number"
                          min={0}
                          max={500}
                          value={rowHeight}
                          onChange={(e) => {
                            const val = Math.max(0, parseInt(e.target.value) || 0);
                            const newHeights = [...rowHeights];
                            // S'assurer que le tableau a la bonne taille
                            while (newHeights.length < rows) newHeights.push(0);
                            newHeights[rowIndex] = val;
                            setRowHeights(newHeights);
                            updateParent({ row_heights: newHeights });
                          }}
                          className="w-20 h-8 text-sm"
                          data-testid={`input-row-height-${rowIndex}`}
                        />
                        <input
                          type="range"
                          min="0"
                          max="500"
                          step="10"
                          value={rowHeight}
                          onChange={(e) => {
                            const val = parseInt(e.target.value);
                            const newHeights = [...rowHeights];
                            while (newHeights.length < rows) newHeights.push(0);
                            newHeights[rowIndex] = val;
                            setRowHeights(newHeights);
                            updateParent({ row_heights: newHeights });
                          }}
                          className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
                        />
                        <span className="text-xs text-muted-foreground w-14 text-right">
                          {rowHeight === 0 ? 'auto' : `${rowHeight}px`}
                        </span>
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-muted-foreground">
                  Définissez une hauteur différente pour chaque ligne.
                  <br />
                  <span className="text-amber-600">Priorité : row_heights &gt; cell_height &gt; auto</span>
                </p>
              </div>
            )}
          </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* 🔵 CONTENU — Style des cellules                            */}
      {/* ═══════════════════════════════════════════════════════════ */}
      <div className="border rounded-lg p-4 bg-sky-50/30 space-y-4">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-sky-500" />
          <Label className="text-base font-semibold block">Contenu</Label>
          <span className="text-xs text-muted-foreground">Style des cellules</span>
        </div>

        {/* Padding interne des cellules */}
        <div className="space-y-2">
          <Label htmlFor="internal-padding">
            Padding interne des cellules (px)
            <span className="text-xs text-muted-foreground ml-2">Espace à l'intérieur de chaque cellule</span>
          </Label>
          <div className="flex items-center gap-3">
            <Input
              id="internal-padding"
              type="number"
              min={0}
              max={100}
              value={internalPadding}
              onChange={(e) => {
                const val = Math.max(0, parseInt(e.target.value) || 0);
                setInternalPadding(val);
                updateParent({ internal_padding: val });
              }}
              className="w-24"
            />
            <input
              type="range"
              min="0"
              max="100"
              value={internalPadding}
              onChange={(e) => {
                const val = parseInt(e.target.value);
                setInternalPadding(val);
                updateParent({ internal_padding: val });
              }}
              className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
            />
            <span className="text-sm text-muted-foreground w-16 text-right">
              {internalPadding}px
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            Espace interne par défaut pour toutes les cellules (modifiable individuellement)
          </p>
        </div>

        {/* Border Radius des cellules */}
        <div className="space-y-2">
          <Label htmlFor="cellBorderRadius">
            Arrondi des cellules (px)
            <span className="text-xs text-muted-foreground ml-2">Border-radius uniforme</span>
          </Label>
          <div className="flex items-center gap-3">
            <Input
              id="cellBorderRadius"
              type="number"
              min={0}
              max={50}
              value={cellBorderRadius}
              onChange={(e) => {
                const val = Math.max(0, parseInt(e.target.value) || 0);
                setCellBorderRadius(val);
                updateParent({ cell_border_radius: val });
              }}
              className="w-24"
              data-testid="input-cell-border-radius"
            />
            <input
              type="range"
              min="0"
              max="50"
              value={cellBorderRadius}
              onChange={(e) => {
                const val = parseInt(e.target.value);
                setCellBorderRadius(val);
                updateParent({ cell_border_radius: val });
              }}
              className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
            />
            <span className="text-sm text-muted-foreground w-16 text-right">
              {cellBorderRadius}px
            </span>
          </div>
        </div>
      </div>

      {/* Actions de fusion */}
      {selectedCells.length > 0 && (
        <div className="border rounded-lg p-3 bg-blue-50">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">
              {selectedCells.length} cellule(s) sélectionnée(s)
            </span>
            <div className="flex gap-2">
              {canMergeCells() && (
                <Button
                  type="button"
                  size="sm"
                  onClick={mergeCells}
                >
                  <Merge className="w-4 h-4 mr-1" />
                  Fusionner
                </Button>
              )}
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setSelectedCells([])}
              >
                Annuler
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Grille visuelle */}
      <div className="border rounded-lg p-4">
        <Label className="text-base font-semibold mb-3 block">
          Grille éditable 
          <span className="text-xs text-muted-foreground ml-2">(Clic simple = éditer | Shift+Clic = sélectionner pour fusion)</span>
        </Label>
        <div 
          className="grid gap-2 bg-white p-4 rounded border"
          style={{
            gridTemplateColumns: `repeat(${columns}, 1fr)`,
            gridTemplateRows: `repeat(${rows}, 100px)`
          }}
        >
          {cells.filter(cell => !cell.disabled).map((cell) => {
            const isSelected = selectedCells.some(c => c.id === cell.id);
            
            // Calculer le padding pour le preview
            const paddingDetail = cell.data?.padding_detail || { top: 16, right: 16, bottom: 16, left: 16 };
            
            return (
              <div
                key={cell.id}
                className={`border-2 rounded flex items-center justify-center text-center transition-all overflow-hidden ${
                  isSelected 
                    ? 'border-blue-500 bg-blue-100' 
                    : 'border-dashed border-gray-300 hover:border-primary cursor-pointer'
                }`}
                style={{
                  gridColumn: `span ${cell.colspan}`,
                  gridRow: `span ${cell.rowspan}`,
                  backgroundColor: cell.data?.background_color || '#ffffff',
                  position: 'relative'
                }}
                onClick={(e) => {
                  if (e.shiftKey) {
                    toggleCellSelection(cell);
                  } else {
                    handleEditCell(cell);
                  }
                }}
              >
                {/* Indicateurs de padding (guides visuels) */}
                {!isSelected && (
                  <>
                    {/* Padding top */}
                    {paddingDetail.top > 0 && (
                      <div 
                        className="absolute top-0 left-0 right-0 bg-blue-100/50 border-b border-dashed border-blue-300"
                        style={{ height: `${Math.min(paddingDetail.top, 30)}px` }}
                      >
                        <span className="text-[8px] text-blue-600 absolute top-0 left-1/2 -translate-x-1/2">
                          {paddingDetail.top}px
                        </span>
                      </div>
                    )}
                    
                    {/* Padding right */}
                    {paddingDetail.right > 0 && (
                      <div 
                        className="absolute top-0 right-0 bottom-0 bg-blue-100/50 border-l border-dashed border-blue-300"
                        style={{ width: `${Math.min(paddingDetail.right, 30)}px` }}
                      >
                        <span className="text-[8px] text-blue-600 absolute top-1/2 right-0 -translate-y-1/2 rotate-90 origin-center">
                          {paddingDetail.right}px
                        </span>
                      </div>
                    )}
                    
                    {/* Padding bottom */}
                    {paddingDetail.bottom > 0 && (
                      <div 
                        className="absolute bottom-0 left-0 right-0 bg-blue-100/50 border-t border-dashed border-blue-300"
                        style={{ height: `${Math.min(paddingDetail.bottom, 30)}px` }}
                      >
                        <span className="text-[8px] text-blue-600 absolute bottom-0 left-1/2 -translate-x-1/2">
                          {paddingDetail.bottom}px
                        </span>
                      </div>
                    )}
                    
                    {/* Padding left */}
                    {paddingDetail.left > 0 && (
                      <div 
                        className="absolute top-0 left-0 bottom-0 bg-blue-100/50 border-r border-dashed border-blue-300"
                        style={{ width: `${Math.min(paddingDetail.left, 30)}px` }}
                      >
                        <span className="text-[8px] text-blue-600 absolute top-1/2 left-0 -translate-y-1/2 -rotate-90 origin-center">
                          {paddingDetail.left}px
                        </span>
                      </div>
                    )}
                  </>
                )}

                {/* Contenu de la cellule (avec padding appliqué) */}
                <div
                  className="w-full h-full flex items-center justify-center"
                  style={{
                    paddingTop: `${paddingDetail.top}px`,
                    paddingRight: `${paddingDetail.right}px`,
                    paddingBottom: `${paddingDetail.bottom}px`,
                    paddingLeft: `${paddingDetail.left}px`
                  }}
                >
                {/* Preview visuel du contenu */}
                {cell.content_type === 'empty' ? (
                  <div className="text-xs text-muted-foreground">
                    <Edit className="w-4 h-4 mx-auto mb-1" />
                    Cliquer pour éditer
                  </div>
                ) : cell.content_type === 'title' ? (
                  <div className="text-sm font-bold truncate w-full px-1">
                    {cell.data.text || 'Titre'}
                  </div>
                ) : cell.content_type === 'text' ? (
                  <div className="text-xs w-full px-1 space-y-1">
                    {cell.data.title && (
                      <div className="font-semibold truncate">{cell.data.title}</div>
                    )}
                    {cell.data.text && (
                      <div className="text-muted-foreground line-clamp-3 whitespace-pre-wrap">
                        {cell.data.text}
                      </div>
                    )}
                  </div>
                ) : cell.content_type === 'image' ? (
                  <div className="w-full h-full flex items-center justify-center">
                    {(cell.data.uploaded_image_url || cell.data.image_url) ? (
                      <img 
                        src={cell.data.uploaded_image_url || cell.data.image_url} 
                        alt=""
                        className="max-w-full max-h-full object-contain rounded"
                        onError={(e) => {
                          console.error('❌ Admin preview image failed:', cell.data.uploaded_image_url || cell.data.image_url);
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="text-xs text-muted-foreground">📷 Image</div>
                    )}
                  </div>
                ) : cell.content_type === 'button' ? (
                  <div className="px-2 py-1 bg-primary text-primary-foreground rounded text-xs font-medium truncate">
                    {cell.data.text || 'Bouton'}
                  </div>
                ) : cell.content_type === 'video' ? (
                  <div className="text-xs">
                    🎥 {cell.data.video_url ? 'URL vidéo' : cell.data.uploaded_video_url ? 'Vidéo uploadée' : 'Vidéo'}
                  </div>
                ) : (
                  <div className="text-xs">
                    <div className="font-semibold">{cell.content_type}</div>
                  </div>
                )}
                </div>{/* Fin du div contenu avec padding */}
                
                {/* Badge couleur de fond */}
                {cell.data?.background_color && cell.data.background_color !== '#ffffff' && !isSelected && (
                  <div className="absolute top-1 left-1 text-[8px] bg-white/90 px-1 py-0.5 rounded border border-gray-300 flex items-center gap-1">
                    <div 
                      className="w-2 h-2 rounded-full border border-gray-400"
                      style={{ backgroundColor: cell.data.background_color }}
                    />
                    <span className="font-mono">{cell.data.background_color}</span>
                  </div>
                )}
                
                {/* Indication fusion */}
                {(cell.colspan > 1 || cell.rowspan > 1) && (
                  <div className="absolute bottom-1 right-1 text-xs text-blue-600 bg-white px-1 rounded">
                    {cell.colspan}×{cell.rowspan}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Modal d'édition de cellule */}
      <Dialog open={showCellModal} onOpenChange={setShowCellModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
          {/* Header fixe */}
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Éditer la cellule</DialogTitle>
          </DialogHeader>

          {/* Body scrollable */}
          <div className="flex-1 overflow-y-auto pr-2 space-y-4">
            <div className="space-y-2">
              <Label>Type de contenu *</Label>
              <Select
                value={editingCell?.content_type || 'empty'}
                onValueChange={handleCellContentTypeChange}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="empty">Vide</SelectItem>
                  <SelectItem value="title">Titre</SelectItem>
                  <SelectItem value="text">Texte</SelectItem>
                  <SelectItem value="image">Image</SelectItem>
                  <SelectItem value="button">Bouton</SelectItem>
                  <SelectItem value="video">Vidéo</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {renderCellContentEditor()}

            {/* Options de personnalisation de la cellule */}
            <div className="border-t pt-4 space-y-4">
              <h3 className="text-sm font-semibold text-muted-foreground">Options de personnalisation</h3>
              
              {/* Couleur de fond */}
              <div className="space-y-2">
                <Label htmlFor="cell-bg-color">Couleur de fond (optionnel)</Label>
                <div className="flex gap-2 items-center">
                  <Input
                    id="cell-bg-color"
                    type="color"
                    value={editingCell?.data?.background_color || '#ffffff'}
                    onChange={(e) => handleCellDataChange('background_color', e.target.value)}
                    className="w-20 h-10 cursor-pointer"
                  />
                  <Input
                    type="text"
                    value={editingCell?.data?.background_color || ''}
                    onChange={(e) => handleCellDataChange('background_color', e.target.value)}
                    placeholder="#FFFFFF ou transparent"
                    className="flex-1"
                  />
                  {editingCell?.data?.background_color && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => handleCellDataChange('background_color', '')}
                    >
                      Effacer
                    </Button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Laisser vide pour fond transparent
                </p>
              </div>

              {/* Padding interne détaillé */}
              <div className="space-y-3">
                <Label>Espacement interne (padding en pixels)</Label>
                
                {/* Mode rapide (preset) */}
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">Mode rapide :</p>
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      type="button"
                      size="sm"
                      variant={JSON.stringify(editingCell?.data?.padding_detail) === JSON.stringify({top: 0, right: 0, bottom: 0, left: 0}) ? 'default' : 'outline'}
                      onClick={() => handleCellDataChange('padding_detail', {top: 0, right: 0, bottom: 0, left: 0})}
                    >
                      Aucun (0px)
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={JSON.stringify(editingCell?.data?.padding_detail) === JSON.stringify({top: 8, right: 8, bottom: 8, left: 8}) ? 'default' : 'outline'}
                      onClick={() => handleCellDataChange('padding_detail', {top: 8, right: 8, bottom: 8, left: 8})}
                    >
                      Petit (8px)
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={JSON.stringify(editingCell?.data?.padding_detail) === JSON.stringify({top: 16, right: 16, bottom: 16, left: 16}) ? 'default' : 'outline'}
                      onClick={() => handleCellDataChange('padding_detail', {top: 16, right: 16, bottom: 16, left: 16})}
                    >
                      Moyen (16px)
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={JSON.stringify(editingCell?.data?.padding_detail) === JSON.stringify({top: 24, right: 24, bottom: 24, left: 24}) ? 'default' : 'outline'}
                      onClick={() => handleCellDataChange('padding_detail', {top: 24, right: 24, bottom: 24, left: 24})}
                    >
                      Grand (24px)
                    </Button>
                  </div>
                </div>

                {/* Mode détaillé */}
                <div className="border rounded-lg p-3 bg-gray-50">
                  <p className="text-xs font-medium mb-3">Mode détaillé :</p>
                  <div className="grid grid-cols-2 gap-3">
                    {/* Haut */}
                    <div className="space-y-1">
                      <Label htmlFor="padding-top" className="text-xs">Haut (top)</Label>
                      <Input
                        id="padding-top"
                        type="number"
                        min="0"
                        step="1"
                        value={editingCell?.data?.padding_detail?.top || 16}
                        onChange={(e) => {
                          const newPadding = {
                            ...editingCell?.data?.padding_detail,
                            top: Math.max(0, parseInt(e.target.value) || 0)
                          };
                          handleCellDataChange('padding_detail', newPadding);
                        }}
                        className="h-9"
                        placeholder="0"
                      />
                    </div>

                    {/* Droite */}
                    <div className="space-y-1">
                      <Label htmlFor="padding-right" className="text-xs">Droite (right)</Label>
                      <Input
                        id="padding-right"
                        type="number"
                        min="0"
                        step="1"
                        value={editingCell?.data?.padding_detail?.right || 16}
                        onChange={(e) => {
                          const newPadding = {
                            ...editingCell?.data?.padding_detail,
                            right: Math.max(0, parseInt(e.target.value) || 0)
                          };
                          handleCellDataChange('padding_detail', newPadding);
                        }}
                        className="h-9"
                        placeholder="0"
                      />
                    </div>

                    {/* Bas */}
                    <div className="space-y-1">
                      <Label htmlFor="padding-bottom" className="text-xs">Bas (bottom)</Label>
                      <Input
                        id="padding-bottom"
                        type="number"
                        min="0"
                        step="1"
                        value={editingCell?.data?.padding_detail?.bottom || 16}
                        onChange={(e) => {
                          const newPadding = {
                            ...editingCell?.data?.padding_detail,
                            bottom: Math.max(0, parseInt(e.target.value) || 0)
                          };
                          handleCellDataChange('padding_detail', newPadding);
                        }}
                        className="h-9"
                        placeholder="0"
                      />
                    </div>

                    {/* Gauche */}
                    <div className="space-y-1">
                      <Label htmlFor="padding-left" className="text-xs">Gauche (left)</Label>
                      <Input
                        id="padding-left"
                        type="number"
                        min="0"
                        step="1"
                        value={editingCell?.data?.padding_detail?.left || 16}
                        onChange={(e) => {
                          const newPadding = {
                            ...editingCell?.data?.padding_detail,
                            left: Math.max(0, parseInt(e.target.value) || 0)
                          };
                          handleCellDataChange('padding_detail', newPadding);
                        }}
                        className="h-9"
                        placeholder="0"
                      />
                    </div>
                  </div>

                  {/* Preview visuel */}
                  <div className="mt-3 p-2 bg-white rounded border">
                    <div 
                      className="bg-blue-50 border-2 border-dashed border-blue-300 flex items-center justify-center text-xs text-blue-600"
                      style={{
                        paddingTop: `${editingCell?.data?.padding_detail?.top || 16}px`,
                        paddingRight: `${editingCell?.data?.padding_detail?.right || 16}px`,
                        paddingBottom: `${editingCell?.data?.padding_detail?.bottom || 16}px`,
                        paddingLeft: `${editingCell?.data?.padding_detail?.left || 16}px`,
                        minHeight: '60px'
                      }}
                    >
                      Contenu de la cellule
                    </div>
                    <p className="text-xs text-muted-foreground text-center mt-2">
                      Aperçu du padding : {editingCell?.data?.padding_detail?.top || 16}px / {editingCell?.data?.padding_detail?.right || 16}px / {editingCell?.data?.padding_detail?.bottom || 16}px / {editingCell?.data?.padding_detail?.left || 16}px
                    </p>
                  </div>
                </div>
              </div>

              {/* Action diviser cellule fusionnée */}
              {editingCell && (editingCell.colspan > 1 || editingCell.rowspan > 1) && (
                <div className="border rounded-lg p-3 bg-orange-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">Cellule fusionnée</p>
                      <p className="text-xs text-muted-foreground">
                        {editingCell.colspan}×{editingCell.rowspan} cellules fusionnées
                      </p>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        splitCell(editingCell);
                        setShowCellModal(false);
                      }}
                    >
                      <Split className="w-4 h-4 mr-1" />
                      Diviser
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer fixe */}
          <div className="flex justify-end gap-2 pt-4 border-t flex-shrink-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowCellModal(false)}
            >
              Annuler
            </Button>
            <Button onClick={handleSaveCell}>
              Enregistrer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ModularGridEditor;
